import { yupResolver } from '@hookform/resolvers/yup'
import Drawer from '@material-ui/core/Drawer'
import CloseIcon from '@material-ui/icons/Close'
import { maxMessageLength } from '@shared/core/events'
import type { SupportedLanguage } from '@shared/core/localized-defaults'
import { MESSAGE_TEMPLATE_VARCHAR_MAX, MessageTemplateDto } from '@shared/core/message-templates'
import { MessageType } from '@shared/core/reminders'
import { areArraysEqual, extractErrorMessage } from '@shared/util'
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Controller, useForm } from 'react-hook-form'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { Button, FormControl, InfoModal, SelectField, TextField, Typography } from 'src/modules/core/components'
import { CantRemoveCustomOffsetModal } from 'src/modules/custom-offsets/cant-remove-custom-offset-modal/cant-remove-custom-offset-modal'
import { useAddCustomOffset } from 'src/modules/custom-offsets/hooks/use-add-custom-offset'
import { useConfirmationOffsets } from 'src/modules/custom-offsets/hooks/use-confirmation-offsets'
import { useFollowUpOffsets } from 'src/modules/custom-offsets/hooks/use-follow-up-offsets'
import { useReminderOffsets } from 'src/modules/custom-offsets/hooks/use-reminder-offsets'
import { useRemoveCustomOffset } from 'src/modules/custom-offsets/hooks/use-remove-custom-offset'
import { generateEstimatedEventMessage } from 'src/modules/event/utils/message-builder'
import { useCalendarsWithTemplates } from 'src/modules/message-templates/hooks/use-calendars-with-templates'
import {
  useCreateMessageTemplate,
  useDeleteMessageTemplate,
  useSetAsDefault,
  useUpdateMessageTemplate,
} from 'src/modules/message-templates/hooks/use-message-templates'
import { useUserPreferences } from 'src/modules/preferences/hooks/use-user-preferences'
import { ReminderTypeSection } from 'src/modules/reminder/components/reminder-types/reminder-type-section'
import { buildMessageFieldPlaceholder } from 'src/modules/reminder/utils/dynamic-data-token-hints.utils'
import { CustomOffsetsService } from 'src/services/custom-offsets.service'
import { MessageLanguageNotice } from '../../components/message-language-notice'
import { useMessageLanguage } from '../../hooks/use-message-language'
import { useProfile } from '../../hooks/use-profile'
import { resolveProfileMessageLanguage } from '../../utils/resolve-profile-message-language'
import { DeleteTemplate } from './delete-template'
import classes from './template-form-drawer.module.scss'
import { schema } from './template-form-drawer.schema'
import { TemplateLanguageField } from './template-language-field'
import { TemplateNameField } from './template-name-field'

interface TemplateFormDrawerProps {
  isOpen: boolean
  onClose: () => void
  template?: MessageTemplateDto
  stackOverModal?: boolean
}

/*
 * The core Modal's backdrop sits at z-index 1301, above MUI's 1300. At that same z-index, DOM
 * order stacks the portaled drawer over the Modal and its confirm modals, portaled after it,
 * over the drawer. Never `disablePortal`: MUI marks the drawer's body-level ancestor
 * aria-hidden while it is open, which would hide the drawer itself from screen readers.
 */
const STACKED_OVER_MODAL_STYLE = { zIndex: 1301 }

interface TemplateFormData {
  template_name: string
  sender_name: string
  location: string
  confirmation_enabled: boolean
  confirmation_message: string
  confirmation_offsets: number[]
  reminder_enabled: boolean
  reminder_message: string
  reminder_offsets: number[]
  follow_up_enabled: boolean
  follow_up_message: string
  follow_up_offsets: number[]
  is_default: boolean
  default_for_calendars: string[]
  language: SupportedLanguage | null
}

const DEFAULT_TEMPLATE_VALUES: Partial<TemplateFormData> = {
  is_default: false,
  default_for_calendars: [],
  confirmation_offsets: [],
  reminder_offsets: [],
  follow_up_offsets: [],
  confirmation_enabled: false,
  reminder_enabled: true,
  follow_up_enabled: false,
  language: null,
}

interface TemplateChanges {
  onlyNameChanged: boolean
  hasTemplateChanges: boolean
  hasCalendarChanges: boolean
  hasPendingOffsetRemovals: boolean
  hasChanges: boolean
}

interface PendingOffsetRemoval {
  id: string
  offsetInMs: number
  type: MessageType
}

function toTemplateFormValues(template: MessageTemplateDto): Partial<TemplateFormData> {
  const { id: _id, created_at: _ca, updated_at: _ua, deleted_at: _da, user_id: _uid, ...fields } = template
  return fields
}

export function TemplateFormDrawer({ isOpen, onClose, template, stackOverModal = false }: TemplateFormDrawerProps) {
  const profile = useProfile()
  const accountLanguage = useMessageLanguage()
  const preferences = useUserPreferences()
  const createTemplate = useCreateMessageTemplate()
  const updateTemplate = useUpdateMessageTemplate()
  const setAsDefault = useSetAsDefault()
  const deleteTemplate = useDeleteMessageTemplate()
  const addCustomOffset = useAddCustomOffset()
  const removeCustomOffset = useRemoveCustomOffset()
  const confirmationOffsets = useConfirmationOffsets()
  const reminderOffsets = useReminderOffsets()
  const followUpOffsets = useFollowUpOffsets()
  const [showDefaultConfirmModal, setShowDefaultConfirmModal] = useState(false)
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false)
  const [formDataToSubmit, setFormDataToSubmit] = useState<TemplateFormData | null>(null)
  const [showTemplateMessage, setShowTemplateMessage] = useState(false)
  const [showCalendarMessage, setShowCalendarMessage] = useState(false)
  const [pendingOffsetRemovals, setPendingOffsetRemovals] = useState<PendingOffsetRemoval[]>([])
  const [showCantRemoveModal, setShowCantRemoveModal] = useState(false)

  const { data: calendars = [] } = useCalendars()
  const { data: calendarsWithTemplates = [] } = useCalendarsWithTemplates()

  // Reset pending removals when drawer opens or template changes
  useEffect(() => {
    if (isOpen) {
      setPendingOffsetRemovals([])
    }
  }, [isOpen, template])

  // Filter out pending removals from timing options
  const filteredConfirmationOffsets = useMemo(() => {
    if (!confirmationOffsets) return []
    const pendingRemovalIds = pendingOffsetRemovals
      .filter((removal) => removal.type === 'confirmation')
      .map((removal) => removal.id)
    return confirmationOffsets.filter((offset) => !pendingRemovalIds.includes(offset.id))
  }, [confirmationOffsets, pendingOffsetRemovals])

  const filteredReminderOffsets = useMemo(() => {
    if (!reminderOffsets) return []
    const pendingRemovalIds = pendingOffsetRemovals
      .filter((removal) => removal.type === 'reminder')
      .map((removal) => removal.id)
    return reminderOffsets.filter((offset) => !pendingRemovalIds.includes(offset.id))
  }, [reminderOffsets, pendingOffsetRemovals])

  const filteredFollowUpOffsets = useMemo(() => {
    if (!followUpOffsets) return []
    const pendingRemovalIds = pendingOffsetRemovals
      .filter((removal) => removal.type === 'follow_up')
      .map((removal) => removal.id)
    return followUpOffsets.filter((offset) => !pendingRemovalIds.includes(offset.id))
  }, [followUpOffsets, pendingOffsetRemovals])

  const handleAddCustomOffset = async (offsetInMs: number, type: MessageType): Promise<void> => {
    const key = `${type}_offsets` as keyof TemplateFormData
    return new Promise((resolve, reject) => {
      addCustomOffset.mutate(
        { offset: offsetInMs, messageType: type },
        {
          onSuccess: () => {
            setValue(key, [...new Set([...((watch(key) as number[]) || []), offsetInMs])], {
              shouldDirty: true,
              shouldTouch: true,
            })
            resolve()
          },
          onError: (error: unknown) => {
            reject(new Error(extractErrorMessage(error, 'Failed to add custom offset')))
          },
        },
      )
    })
  }

  const handleRemoveCustomOffset = async (id: string, offsetInMs: number, type: MessageType): Promise<void> => {
    const canRemove = await CustomOffsetsService.canRemoveCustomOffset(id, template?.id)
    if (!canRemove) {
      setShowCantRemoveModal(true)
      return Promise.resolve()
    }

    const key = `${type}_offsets` as keyof TemplateFormData
    setValue(
      key,
      (watch(key) as number[]).filter((v) => v !== offsetInMs),
      { shouldDirty: true, shouldTouch: true },
    )
    setPendingOffsetRemovals((prev) => [...prev, { id, offsetInMs, type }])
    return Promise.resolve()
  }

  // Process pending offset removals after successful form submission
  const processPendingOffsetRemovals = async (): Promise<void> => {
    if (pendingOffsetRemovals.length === 0) {
      return Promise.resolve()
    }

    const removalPromises = pendingOffsetRemovals.map(({ id }) => removeCustomOffset.mutateAsync(id))

    try {
      await Promise.all(removalPromises)
      setPendingOffsetRemovals([])
    } catch (error) {
      // Note: We don't throw here as the main form operation was successful
      // The user can retry by saving again if needed
      console.error('Failed to remove some custom offsets:', error)
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
    trigger,
    setError,
    setValue,
    watch,
  } = useForm<TemplateFormData>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      ...DEFAULT_TEMPLATE_VALUES,
      ...(template ? toTemplateFormValues(template) : {}),
    },
  })

  // Reset form when drawer opens or template changes
  useEffect(() => {
    if (isOpen) {
      reset({
        ...DEFAULT_TEMPLATE_VALUES,
        ...(template ? toTemplateFormValues(template) : {}),
      })
      void trigger()
    }
  }, [isOpen, template, reset, trigger])

  const handleToggle = (field: keyof TemplateFormData) => (enabled: boolean) => {
    setValue(field, enabled, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  const { sender_name, location, confirmation_message, reminder_message, follow_up_message } = watch()
  const templateLanguage = watch('language') ?? null
  const savedTemplateLanguage = template?.language ?? null
  const {
    language: messageLanguage,
    source: messageLanguageSource,
    countryCode: messageLanguageCountryCode,
  } = useMemo(() => resolveProfileMessageLanguage(profile.data, templateLanguage), [profile.data, templateLanguage])

  const {
    message: confirmationMessagePreview,
    messageLength: confirmationMessageLength,
    segmentsCount: confirmationSegmentsCount,
  } = useMemo(
    () =>
      generateEstimatedEventMessage(
        'confirmation',
        {
          ...profile.data,
          default_message_template: {
            sender_name: sender_name,
            location: location,
            confirmation_message: confirmation_message,
          },
        },
        preferences.data,
        templateLanguage,
      ),
    [profile.data, preferences.data, sender_name, location, confirmation_message, templateLanguage],
  )
  const {
    message: reminderMessagePreview,
    messageLength: reminderMessageLength,
    segmentsCount: reminderSegmentsCount,
  } = useMemo(() => {
    return generateEstimatedEventMessage(
      'reminder',
      {
        ...profile.data,
        default_message_template: {
          sender_name: sender_name,
          location: location,
          reminder_message: reminder_message,
        },
      },
      preferences.data,
      templateLanguage,
    )
  }, [profile.data, preferences.data, sender_name, location, reminder_message, templateLanguage])
  const {
    message: followUpMessagePreview,
    messageLength: followUpMessageLength,
    segmentsCount: followUpSegmentsCount,
  } = useMemo(
    () =>
      generateEstimatedEventMessage(
        'follow_up',
        {
          ...profile.data,
          default_message_template: {
            sender_name: sender_name,
            location: location,
            follow_up_message: follow_up_message,
          },
        },
        preferences.data,
        templateLanguage,
      ),
    [profile.data, preferences.data, sender_name, location, follow_up_message, templateLanguage],
  )

  const confirmationMessageFieldPlaceholder = useMemo(
    () => buildMessageFieldPlaceholder('confirmation', messageLanguage),
    [messageLanguage],
  )
  const reminderMessageFieldPlaceholder = useMemo(
    () => buildMessageFieldPlaceholder('reminder', messageLanguage),
    [messageLanguage],
  )
  const followUpMessageFieldPlaceholder = useMemo(
    () => buildMessageFieldPlaceholder('follow_up', messageLanguage),
    [messageLanguage],
  )

  const calendarOptions = useMemo(() => {
    const pre = calendars.map((cal) => {
      return {
        label: cal.title ?? cal.calendar_id,
        value: cal.id,
        isPrimary: cal.primary,
        isDisabled: calendarsWithTemplates.some((cwt) => cwt.calendarId === cal.id && cwt.templateId !== template?.id),
      }
    })
    // Available calendars first, disabled calendars last
    // Primary calendar should come first in its group
    const sortedCalendars = pre.sort((a, b) => {
      if (a.isDisabled && !b.isDisabled) return 1
      if (!a.isDisabled && b.isDisabled) return -1
      if (a.isPrimary && !b.isPrimary) return -1
      if (!a.isPrimary && b.isPrimary) return 1
      return (a.label ?? '').localeCompare(b.label ?? '')
    })
    return sortedCalendars.map((cal) => ({
      label: cal.isDisabled ? `${cal.label} (Used in another template)` : cal.label,
      value: cal.value,
      isDisabled: cal.isDisabled,
    }))
  }, [calendars, calendarsWithTemplates, template?.id])

  const validateMessageLength = (fieldName: string, messageLength: number) => {
    if (messageLength > maxMessageLength) {
      setError(fieldName as keyof TemplateFormData, {
        type: 'custom',
        message: 'Message must be shorter than 320 characters',
      })
      return false
    }
    return true
  }

  const hasFieldChanged = (newData: TemplateFormData, oldData: MessageTemplateDto, field: keyof TemplateFormData) => {
    if (Array.isArray(newData[field]))
      return !areArraysEqual(newData[field] as Array<unknown>, oldData[field] as Array<unknown>)
    return newData[field] !== oldData[field]
  }

  const checkTemplateChanges = (data: TemplateFormData): TemplateChanges | false => {
    if (!template) return false

    const hasChangesToFields = (fields: string[]) => {
      return fields.some((field) => hasFieldChanged(data, template, field as keyof TemplateFormData))
    }

    const nameChanged = hasChangesToFields(['template_name'])
    // An API without the column returns no `language`; treat it as null.
    const languageChanged = (data.language ?? null) !== savedTemplateLanguage
    const hasTemplateChanges =
      languageChanged ||
      hasChangesToFields([
        'sender_name',
        'location',
        'confirmation_enabled',
        'confirmation_message',
        'confirmation_offsets',
        'reminder_enabled',
        'reminder_message',
        'reminder_offsets',
        'follow_up_enabled',
        'follow_up_message',
        'follow_up_offsets',
      ])
    const hasCalendarChanges = hasChangesToFields(['default_for_calendars'])

    const hasPendingOffsetRemovals = pendingOffsetRemovals.length > 0

    return {
      onlyNameChanged: nameChanged && !hasTemplateChanges && !hasCalendarChanges,
      hasTemplateChanges,
      hasCalendarChanges,
      hasPendingOffsetRemovals,
      hasChanges: nameChanged || hasTemplateChanges || hasCalendarChanges || hasPendingOffsetRemovals,
    }
  }

  const handleTemplateUpdate = (data: TemplateFormData) => {
    const changes = checkTemplateChanges(data)
    if (!changes || !changes.hasChanges) {
      return onClose()
    }

    if (!changes.hasTemplateChanges && !changes.hasCalendarChanges) {
      updateTemplate.mutate(
        { id: template!.id, dto: data },
        {
          onSuccess: async () => {
            await processPendingOffsetRemovals()
            onClose()
          },
        },
      )
    } else {
      setShowTemplateMessage(changes.hasTemplateChanges)
      setShowCalendarMessage(changes.hasCalendarChanges)
      setFormDataToSubmit(data)
      setShowSaveConfirmModal(true)
    }
  }

  const onSubmit = (formData: TemplateFormData) => {
    // Always sent, null included: the API keeps the stored language for an omitted field.
    const data = { ...formData, language: formData.language ?? null }
    if (
      !validateMessageLength('confirmation_message', confirmationMessageLength) ||
      !validateMessageLength('reminder_message', reminderMessageLength) ||
      !validateMessageLength('follow_up_message', followUpMessageLength)
    ) {
      return
    }

    if (template) {
      handleTemplateUpdate(data)
    } else {
      createTemplate.mutate(data, {
        onSuccess: async () => {
          await processPendingOffsetRemovals()
          onClose()
        },
      })
    }
  }

  const mutationError = template ? updateTemplate.error : createTemplate.error
  const errorMessage = extractErrorMessage(mutationError, 'An error occurred while saving the template')

  const handleIsDefaultChange = (checked: boolean) => {
    if (checked) {
      setShowDefaultConfirmModal(true)
    } else {
      setValue('is_default', false)
    }
  }

  const handleConfirmDefault = () => {
    if (template) {
      setAsDefault.mutate(template.id, {
        onSuccess: () => {
          setValue('is_default', true)
          setShowDefaultConfirmModal(false)
        },
      })
    }
  }

  const handleDelete = () => {
    if (template) {
      deleteTemplate.mutate(template.id, {
        onSuccess: () => onClose(),
      })
    }
  }

  const handleConfirmSave = () => {
    if (formDataToSubmit && template) {
      updateTemplate.mutate(
        { id: template.id, dto: formDataToSubmit },
        {
          onSuccess: async () => {
            await processPendingOffsetRemovals()
            setShowSaveConfirmModal(false)
            onClose()
          },
        },
      )
    }
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        style={stackOverModal ? STACKED_OVER_MODAL_STYLE : undefined}
      >
        <div className={classes.drawerContent}>
          <div className={classes.header}>
            <Typography variant="h2">{template ? 'Edit Message Template' : 'New Message Template'}</Typography>
            <button onClick={onClose} className={classes.closeButton}>
              <CloseIcon />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
            {mutationError && <div className={classes.errorMessage}>{errorMessage}</div>}

            <TemplateNameField
              label="Template Name"
              error={errors.template_name?.message}
              maxLength={MESSAGE_TEMPLATE_VARCHAR_MAX}
              {...register('template_name')}
              isDefault={watch('is_default')}
              onIsDefaultChange={handleIsDefaultChange}
              isReadOnly={!template || watch('is_default')}
              showSaveFirstText={!template}
              placeholder={`Name your template for your reference`}
            />

            <FormControl
              label="Default for specific calendars"
              error={(errors?.default_for_calendars as any)?.message}
              tooltip="Select calendars to use this template by default for those specific calendars."
            >
              <Controller
                control={control}
                defaultValue={[]}
                name="default_for_calendars"
                render={({ field: { onChange, value, ref } }) => (
                  <SelectField
                    ref={ref}
                    value={calendarOptions?.filter((c) => value?.includes(c.value))}
                    onChange={(val) => onChange(val.map((c) => c.value))}
                    options={calendarOptions}
                    isMulti
                    placeholder="Select calendars (optional)"
                  />
                )}
              />
            </FormControl>

            <TextField
              label="Business Name"
              error={errors.sender_name?.message}
              maxLength={MESSAGE_TEMPLATE_VARCHAR_MAX}
              {...register('sender_name')}
              placeholder={`Your business name (e.g., "ABC Hair Inc")`}
            />

            <TextField
              label="Meeting Location"
              error={errors.location?.message}
              maxLength={MESSAGE_TEMPLATE_VARCHAR_MAX}
              {...register('location')}
              placeholder={`Specify a default location (e.g., "Office" or  "Gym"). You can also use a link.`}
            />

            {profile.data && (
              <Controller
                control={control}
                name="language"
                render={({ field: { onChange, value } }) => (
                  <TemplateLanguageField
                    value={value ?? null}
                    savedValue={template ? savedTemplateLanguage : undefined}
                    accountLanguage={accountLanguage}
                    onChange={onChange}
                  />
                )}
              />
            )}

            <Typography variant="h3" className={classes.messagesTitle}>
              Messages
            </Typography>

            {profile.data && (
              <MessageLanguageNotice
                language={messageLanguage}
                source={messageLanguageSource}
                countryCode={messageLanguageCountryCode}
                usageMode={profile.data.usage_mode}
                accountLanguage={accountLanguage}
                hasUnsavedChanges={isDirty || pendingOffsetRemovals.length > 0}
              />
            )}

            {!watch('confirmation_enabled') && !watch('reminder_enabled') && !watch('follow_up_enabled') && (
              <Typography variant="body" className={classes.noMessageTypesError}>
                At least one message type must be enabled.
              </Typography>
            )}

            <ReminderTypeSection
              type="confirmation"
              title="Confirmation"
              description="Message to send after the reminder is created, or if the date/time of the appointment changes."
              checked={watch('confirmation_enabled')}
              messageField="confirmation_message"
              messageFieldPlaceholder={confirmationMessageFieldPlaceholder}
              messageLanguage={messageLanguage}
              offsetsField="confirmation_offsets"
              timingFieldLabel="Timing: When to send the message after the reminder is saved"
              register={register}
              control={control}
              errors={errors}
              messagePreview={confirmationMessagePreview}
              messageLength={confirmationMessageLength}
              segmentsCount={confirmationSegmentsCount}
              timingOptions={filteredConfirmationOffsets}
              onToggle={handleToggle('confirmation_enabled')}
              onAddOffset={handleAddCustomOffset}
              onRemoveOffset={handleRemoveCustomOffset}
            />

            <ReminderTypeSection
              type="reminder"
              title="Reminder"
              description="Message to send as the appointment approaches."
              checked={watch('reminder_enabled')}
              messageField="reminder_message"
              messageFieldPlaceholder={reminderMessageFieldPlaceholder}
              messageLanguage={messageLanguage}
              offsetsField="reminder_offsets"
              timingFieldLabel="Timing: When to send the message before the appointment starts"
              register={register}
              control={control}
              errors={errors}
              messagePreview={reminderMessagePreview}
              messageLength={reminderMessageLength}
              segmentsCount={reminderSegmentsCount}
              timingOptions={filteredReminderOffsets}
              confirmationsEnabled={profile.data?.appointment_confirmation}
              onToggle={handleToggle('reminder_enabled')}
              onAddOffset={handleAddCustomOffset}
              onRemoveOffset={handleRemoveCustomOffset}
            />

            <ReminderTypeSection
              type="follow_up"
              title="Follow-up"
              description="Message to send after the appointment occurs."
              checked={watch('follow_up_enabled')}
              messageField="follow_up_message"
              messageFieldPlaceholder={followUpMessageFieldPlaceholder}
              messageLanguage={messageLanguage}
              offsetsField="follow_up_offsets"
              timingFieldLabel="Timing: When to send the message after the appointment completes"
              register={register}
              control={control}
              errors={errors}
              messagePreview={followUpMessagePreview}
              messageLength={followUpMessageLength}
              segmentsCount={followUpSegmentsCount}
              timingOptions={filteredFollowUpOffsets}
              onToggle={handleToggle('follow_up_enabled')}
              onAddOffset={handleAddCustomOffset}
              onRemoveOffset={handleRemoveCustomOffset}
            />

            {mutationError && <div className={classes.errorMessage}>{errorMessage}</div>}

            <div className={classes.actions}>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={createTemplate.isLoading || updateTemplate.isLoading}>
                {template ? 'Update' : 'Save'}
              </Button>
            </div>

            {template && !watch('is_default') && (
              <DeleteTemplate onDelete={handleDelete} isDeleting={deleteTemplate.isLoading} />
            )}
          </form>
        </div>
      </Drawer>

      {createPortal(
        <>
          {showDefaultConfirmModal && (
            <InfoModal
              title="Set New Default Template?"
              description={
                <>
                  This will update which template is used by default for new reminders. Existing reminders will not be
                  affected.
                  {setAsDefault.error && (
                    <div className={classes.modalErrorMessage}>
                      {(setAsDefault.error as any)?.message || 'An error occurred while setting the default template.'}
                    </div>
                  )}
                </>
              }
              button="Confirm"
              closeButton="Cancel"
              onClose={() => setShowDefaultConfirmModal(false)}
              onClick={handleConfirmDefault}
              isLoading={setAsDefault.isLoading}
              loadingText="Saving..."
            />
          )}

          {showSaveConfirmModal && (
            <InfoModal
              title="Update Template?"
              description={
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {showTemplateMessage && (
                    <p>
                      This will update the message and reminder timing for any scheduled reminders using this template.
                    </p>
                  )}
                  {showCalendarMessage && (
                    <p>
                      Changing the calendar(s) this template defaults to will only affect future reminders you schedule.
                      Reminders you already scheduled will still use the template that was set at the time of
                      scheduling.
                    </p>
                  )}
                </div>
              }
              button="Confirm"
              closeButton="Cancel"
              onClose={() => setShowSaveConfirmModal(false)}
              onClick={handleConfirmSave}
              isLoading={updateTemplate.isLoading}
              loadingText="Saving..."
            />
          )}
          {showCantRemoveModal && <CantRemoveCustomOffsetModal onClose={() => setShowCantRemoveModal(false)} />}
        </>,
        document.body,
      )}
    </>
  )
}
